import React, { useState, useEffect } from "react";

import styles from "./Pagination.module.scss";

export default function Pagination({
  totalRecords,
  totalPerPage,
  onPageChange,
  totalItems,
  options,
  currentPage,
}) {
  const [paginationInfo, setPaginationInfo] = useState({
    itemsPerPage: totalPerPage || 10,
    currentPage: currentPage || 1,
    offset: 0,
  });

  const [config, setConfig] = useState({
    skipPrevious: true,
    previous: true,
    next: true,
    skipNext: true,
    selectQuantity: true,
    simpleCounting: false,
  });

  const itemOptionsPerPage = [
    { value: 10, name: "10" },
    { value: 25, name: "25" },
    { value: 50, name: "50" },
    { value: 100, name: "100" },
  ];

  useEffect(() => {
    if (totalPerPage !== undefined && currentPage !== undefined) {
      setPaginationInfo({
        itemsPerPage: totalPerPage,
        currentPage: currentPage,
        offset: (currentPage - 1) * totalPerPage,
      });
    }
  }, [currentPage, totalPerPage]);

  useEffect(() => {
    setConfig({
      ...config,
      ...options,
    });
  }, []);

  // Use props directly for calculations to avoid stale state
  const itemsPerPage = totalPerPage !== undefined ? totalPerPage : paginationInfo.itemsPerPage;
  const page = currentPage !== undefined ? currentPage : paginationInfo.currentPage;

  const changeItemsPerPage = (value) => {
    const newItemsPerPage = Number(value);
    const pagination = {
      itemsPerPage: newItemsPerPage,
      currentPage: 1, // Reset to first page when changing items per page
      offset: 0,
    };

    onPageChange?.({ pagination });
    // Don't update local state here - let useEffect sync from props
  };

  const previous = (currentPageNum) => {
    const previousPage = currentPageNum > 1 ? currentPageNum - 1 : 1;

    const pagination = {
      itemsPerPage: itemsPerPage,
      currentPage: previousPage,
      offset: (previousPage - 1) * itemsPerPage,
    };

    onPageChange?.({ pagination });
    // Don't update local state here - let useEffect sync from props
  };

  const next = (currentPageNum) => {
    const maxPageNum = Math.ceil(totalItems / itemsPerPage);
    const nextPage = currentPageNum < maxPageNum ? currentPageNum + 1 : currentPageNum;

    const pagination = {
      itemsPerPage: itemsPerPage,
      currentPage: nextPage,
      offset: (nextPage - 1) * itemsPerPage,
    };

    onPageChange?.({ pagination });
    // Don't update local state here - let useEffect sync from props
  };

  const firstOffset = () => {
    const pagination = {
      itemsPerPage: itemsPerPage,
      currentPage: 1,
      offset: 0,
    };

    onPageChange?.({ pagination });
    // Don't update local state here - let useEffect sync from props
  };

  const lastOffset = () => {
    const lastPage = Math.ceil(totalItems / itemsPerPage);

    const pagination = {
      itemsPerPage: itemsPerPage,
      currentPage: lastPage,
      offset: (lastPage - 1) * itemsPerPage,
    };

    onPageChange?.({ pagination });
    // Don't update local state here - let useEffect sync from props
  };
  
  const maxPage = Math.ceil(totalItems / itemsPerPage);
  const isLastPage = page >= maxPage;
  const isFirstPage = page === 1;

  // Calculate from and to values (1-based indexing)
  const from = isFirstPage
    ? totalItems > 0
      ? 1
      : 0
    : (page - 1) * itemsPerPage + 1;
  const to = Math.min(
    page * itemsPerPage,
    totalItems
  );

  return (
    <div className={styles["pagination-container"]}>
      <div className={styles["pagination-info"]}>
        {config?.selectQuantity && (
          <>
            <span>Sayfa başına kayıt:</span>
            <select
              value={itemsPerPage}
              onChange={(event) => changeItemsPerPage(event.target.value)}
            >
              {itemOptionsPerPage.map((option, index) => (
                <option key={index} value={option.value}>
                  {option.name}
                </option>
              ))}
            </select>
          </>
        )}
        {!config?.simpleCounting ? (
          <span>
            {totalItems} kayıttan {from}-{to} arası
          </span>
        ) : (
          <span>
            {totalItems} kayıttan {to} arası
          </span>
        )}
      </div>
      <div className={styles["pagination-actions"]}>
        {config?.skipPrevious && (
          <button
            onClick={() => firstOffset()}
            disabled={isFirstPage}
            title="İlk Sayfa"
          >
            ⟪
          </button>
        )}
        {config?.previous && (
          <button
            onClick={() => previous(page)}
            disabled={isFirstPage}
            title="Önceki Sayfa"
          >
            ⟨
          </button>
        )}
        {config?.next && (
          <button
            onClick={() => next(page)}
            disabled={isLastPage}
            title="Sonraki Sayfa"
          >
            ⟩
          </button>
        )}
        {config?.skipNext && (
          <button
            onClick={() => lastOffset()}
            disabled={isLastPage}
            title="Son Sayfa"
          >
            ⟫
          </button>
        )}
      </div>
    </div>
  );
}
